import { createApi } from '@reduxjs/toolkit/query/react';

import { axiosBaseQuery } from '@/api/api';
import { ENDPOINT } from '@/constants/endpoints';

export type CreateOrganizationPayload = {
  name: string;
  industry: string;
  languages: string[];
  dataRegion: 'us' | 'eu';
};

export type BillingSetupStatus = {
  plan?: string;
  setup: boolean;
  status?: string;
};

export type SetupStatus = {
  billing?: BillingSetupStatus;
  complete?: boolean;
  messagingConnected?: {
    setup: boolean;
    platform?: 'SLACK' | 'TEAM' | 'TEAMS';
  };
  hris?: {
    setup: boolean;
    initialSyncPending?: boolean;
    name?: string;
    slug?: string;
    squareImage?: string;
  };
  sso?: {
    setup: boolean;
    domain?: string;
    ssoConnected?: string;
  };
};

export type SetupProgressCardKey = 'sso' | 'hris' | 'messagingConnected' | 'billing' | 'branding';

export const DELIVERY_PAUSED_REASONS = [
  'manual',
  'subscription_canceled',
  'subscription_past_due',
  'subscription_unpaid',
  'subscription_paused',
] as const;

export type DeliveryPausedReason = (typeof DELIVERY_PAUSED_REASONS)[number];

export type DeliveryStatus = {
  status: 'active' | 'paused' | 'setup_incomplete';
  pausedReason?: DeliveryPausedReason;
};

export type Organization = {
  id: string;
  name: string;
  industry: string;
  logoUrl: string | null;
  languages: string[];
  setupStatus: SetupStatus;
  deliveryStatus?: DeliveryStatus;
};

type OrganizationByIdResponse = {
  orgId: string;
};

type UpdateOrganizationPayload = {
  name: string;
  industry: string;
  languages: string[];
  logo: string;
};

export const organizationApi = createApi({
  reducerPath: 'organizationApi',
  baseQuery: axiosBaseQuery,
  tagTypes: ['Organization'],
  endpoints: builder => ({
    getOrganization: builder.query<Organization, void>({
      query: () => ({
        url: ENDPOINT.ORGANIZATION.GET(),
        method: 'GET',
      }),
      providesTags: ['Organization'],
    }),
    getOrganizationById: builder.query<OrganizationByIdResponse, void>({
      query: () => ({
        url: ENDPOINT.ORGANIZATION.GET_BY_ID(),
        method: 'GET',
      }),
      providesTags: ['Organization'],
    }),
    createOrganization: builder.mutation<Organization, CreateOrganizationPayload>({
      query: body => ({
        url: ENDPOINT.ORGANIZATION.CREATE(),
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Organization'],
    }),
    updateOrganization: builder.mutation<void, Partial<UpdateOrganizationPayload>>({
      query: body => ({
        url: ENDPOINT.ORGANIZATION.UPDATE(),
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Organization'],
    }),
  }),
});

export const {
  useCreateOrganizationMutation,
  useGetOrganizationQuery,
  useUpdateOrganizationMutation,
  useGetOrganizationByIdQuery,
} = organizationApi;
